import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { endpoints } from '../../utils/endpoints';
import {
    CommunicationChat,
    CommunicationChatMessage,
    CommunicationChatMessagePin,
    CommunicationChatMessages,
} from '../models/api/responses/chat.response';

@Injectable({
    providedIn: 'root'
})
export class CommunicationChatService {
    private http = inject(HttpClient);


    /**
     * Fetch chats by target ID
     * @param targetId The target ID to fetch chats for
     * @returns An observable of communication chats
     */
    fetchChatsByTargetId(targetId: string): Observable<CommunicationChat[]> {
        return this.http.get<CommunicationChat[]>(
            endpoints.communications.children.chat.children.getChatsByTargetId(
                targetId
            )
        );
    }

    /**
     * Fetch chat information
     * @param chatId The chat ID to fetch information for
     * @returns An observable of communication chat
     */
    fetchChatInfo(chatId: string): Observable<CommunicationChat> {
        return this.http.get<CommunicationChat>(
            endpoints.communications.children.chat.children.getChatInfoUrl(
                chatId
            )
        );
    }

    /**
     * Send a message to a chat
     * @param chatId The chat ID to send the message to
     * @param message The message text to send
     * @returns An observable of the response
     */
    sendMessage(chatId: string, message: string): Observable<unknown> {
        return this.http.post<unknown>(
            endpoints.communications.children.chat.children.getSendMessageToTheChatUrl(
                chatId
            ),
            {
                message,
            }
        );
    }

    /**
     * Fetch messages for a chat
     * @param chatId The chat ID to fetch messages for
     * @param lastMessageId The ID of the last message loaded (for pagination)
     * @param messagesLimit The maximum number of messages to fetch
     * @returns An observable of communication chat messages
     */
    fetchChatMessages(
        chatId: string,
        lastMessageId: number | undefined,
        messagesLimit = 50
    ): Observable<CommunicationChatMessages> {
        let params = new HttpParams();

        if (lastMessageId) {
            params = params.append('OffsetId', lastMessageId);
        }

        params = params.append('Limit', messagesLimit);

        return this.http.get<CommunicationChatMessages>(
            endpoints.communications.children.chat.children.getChatMessagesUrl(
                chatId
            ),
            {
                params,
            }
        );
    }
    /**
     * Fetch pinned messages for a chat
     * @param chatId The chat ID to fetch pinned messages for
     * @returns An observable of pinned chat messages
     */
    fetchPinnedChatMessages(
        chatId: string
    ): Observable<CommunicationChatMessagePin[]> {
        return this.http.get<CommunicationChatMessagePin[]>(
            endpoints.communications.children.chat.children.getPinnedChatMessagesUrl(
                chatId
            )
        );
    }
    /**
     * Pin a message in a chat
     * @param chatId The chat ID
     * @param messageId The message ID to pin
     * @returns An observable of pinned chat messages
     */
    pinChatMessages(
        chatId: string,
        messageId: string
    ): Observable<CommunicationChatMessagePin[]> {
        return this.http.post<CommunicationChatMessagePin[]>(
            endpoints.communications.children.chat.children.pinChatMessage(
                chatId
            ),
            { pinnedMessageId: messageId }
        );
    }
    /**
     * Delete a pinned message from a chat
     * @param chatId The chat ID
     * @param pinId The pin ID to delete
     * @returns An observable of the response
     */
    deletePinChatMessages(
        chatId: string,
        pinId: string
    ): Observable<unknown> {
        return this.http.delete<unknown>(
            endpoints.communications.children.chat.children.deletePinChatMessage(
                chatId,
                pinId
            )
        );
    }
    /**
     * Update a message in a chat
     * @param chatId The chat ID
     * @param messageId The message ID to update
     * @param messageText The new message text
     * @returns An observable of the updated message
     */
    updateChatMessages(
        chatId: string,
        messageId: string,
        messageText: string
    ): Observable<CommunicationChatMessage> {
        return this.http.put<CommunicationChatMessage>(
            endpoints.communications.children.chat.children.updateChatMessage(
                chatId,
                messageId
            ),
            { message: messageText, messageId }
        );
    }
    /**
     * Delete a message from a chat
     * @param chatId The chat ID
     * @param messageId The message ID to delete
     * @returns An observable of the response
     */
    deleteChatMessages(
        chatId: string,
        messageId: string
    ): Observable<unknown> {
        return this.http.delete<unknown>(
            endpoints.communications.children.chat.children.deleteChatMessage(
                chatId,
                messageId
            )
        );
    }
    /**
     * Reply to a message in a chat
     * @param chatId The chat ID
     * @param message The reply message text
     * @param parentId The ID of the message being replied to
     * @returns An observable of the response
     */
    replyToChatMessages(
        chatId: string,
        message: string,
        parentId: number
    ): Observable<unknown> {
        return this.http.post<unknown>(
            endpoints.communications.children.chat.children.getSendMessageToTheChatUrl(
                chatId
            ),
            {
                message,
                parentId
            }
        );
    }

}
